/**
 * §14.8 screen 9 — Masʼul ijrochilar.
 *
 * ===========================================================================
 * COMPONENT METRICS, SORTABLE. NO SCORE, NO RATING, NO RANK.
 * ===========================================================================
 *
 * §14.2.5 refuses a composite score, and this screen is where that refusal is
 * either honoured or quietly undone. A single number ordering government staff
 * is an HR instrument, and the data cannot support one honestly: an appeal's
 * DIFFICULTY is nowhere in it, so any weighting of "closed" against "median
 * days" penalises whoever draws the hard cases.
 *
 * So the table shows the components, each labelled, and the READER chooses the
 * ordering by clicking a column. That is a deliberate transfer of judgement:
 * sorting by "overdue" answers "where is the backlog?", sorting by "closed"
 * answers "who has been busy?", and the screen never merges those two questions
 * into one league table. The note under the title says so in the staff
 * member's own language — the people in this table can be shown it.
 *
 * The default sort is by NAME, not by any metric. A table that opens sorted by
 * a performance column has published a ranking whether or not it computed one.
 *
 * Capability `analytics.staff` — `admin` only, org-wide (§14.10.7).
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { ApiError, api, apiBaseUrl, errorKey, jsonBody, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatMetric, formatPercent, fromDateInput } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Spinner,
  Table,
  Td,
  Th,
  TextInput,
  Tr,
  WarningBanner,
} from '../ui/index.jsx';

type ExecutorStats = components['schemas']['AdminExecutorStatsResponse'];
type Executor = components['schemas']['AdminExecutorItem'];

/**
 * The sortable columns. `full_name` leads and is the default — see the header.
 *
 * Each entry names the FIELD, not a weight: there is no arithmetic here that
 * combines two of them, and adding one would be adding the score this screen
 * exists to refuse.
 */
const COLUMNS = [
  { key: 'full_name', labelKey: 'admin.executors.name', numeric: false },
  { key: 'assigned_total', labelKey: 'admin.executors.assignedTotal', numeric: true },
  { key: 'open', labelKey: 'admin.executors.open', numeric: true },
  { key: 'overdue_open', labelKey: 'admin.executors.overdueOpen', numeric: true },
  { key: 'answered', labelKey: 'admin.executors.answered', numeric: true },
  { key: 'closed', labelKey: 'admin.executors.closed', numeric: true },
  { key: 'reopened', labelKey: 'admin.executors.reopened', numeric: true },
  { key: 'median_days_to_answer', labelKey: 'admin.executors.medianDays', numeric: true },
  { key: 'on_time_pct', labelKey: 'admin.executors.onTimePct', numeric: true },
] as const;

type SortKey = (typeof COLUMNS)[number]['key'];

interface Bounds {
  from: string;
  to: string;
}

const EMPTY_BOUNDS: Bounds = { from: '', to: '' };

/**
 * Sorts, keeping ABSENT metrics last in both directions.
 *
 * A staff member who has closed nothing has no `on_time_pct` — the API omits
 * it rather than sending `0` (honesty rule 2). Sorting `undefined` as 0 would
 * put them at the bottom of an ascending "on time" sort as though they were the
 * worst performer, which is the false claim the omission exists to prevent. So
 * missing values sink to the end regardless of direction, where "no data" is
 * what the empty cell already says.
 */
function sortExecutors(items: Executor[], key: SortKey, ascending: boolean): Executor[] {
  return [...items].sort((a, b) => {
    const left = a[key as keyof Executor] as string | number | undefined;
    const right = b[key as keyof Executor] as string | number | undefined;
    if (left === undefined && right === undefined) return 0;
    if (left === undefined) return 1;
    if (right === undefined) return -1;
    if (typeof left === 'string' && typeof right === 'string') {
      return ascending ? left.localeCompare(right) : right.localeCompare(left);
    }
    return ascending ? Number(left) - Number(right) : Number(right) - Number(left);
  });
}

export function ExecutorsScreen(): React.JSX.Element {
  const { t } = useI18n();
  const { expire, allows } = useSession();

  const [bounds, setBounds] = useState<Bounds>(EMPTY_BOUNDS);
  const [applied, setApplied] = useState<Bounds>(EMPTY_BOUNDS);
  const [stats, setStats] = useState<ExecutorStats | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('full_name');
  const [ascending, setAscending] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = fromDateInput(applied.from);
      const to = fromDateInput(applied.to, true);
      const result = await api().GET('/admin/stats/executors', {
        params: { query: { ...(from ? { from } : {}), ...(to ? { to } : {}) } },
      });
      setStats(jsonBody(unwrap(result)));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        expire();
        return;
      }
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    } finally {
      setLoading(false);
    }
  }, [applied, expire, t]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggleSort(key: SortKey): void {
    if (key === sortKey) {
      setAscending((value) => !value);
      return;
    }
    setSortKey(key);
    // A metric column opens DESCENDING (largest first, which is what a reader
    // clicking "overdue" is asking for); the name column opens A→Z.
    setAscending(key === 'full_name');
  }

  const items = stats ? sortExecutors(stats.items, sortKey, ascending) : [];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-brand-900">{t('admin.executors.title')}</h1>
      </div>

      {/* The refusal, stated on the screen rather than only in the code. */}
      <WarningBanner>{t('admin.executors.noScoreNotice')}</WarningBanner>

      <Card
        title={t('admin.stats.filters')}
        actions={
          <div className="flex items-center gap-2">
            {allows('analytics.export') ? (
              <a
                href={`${apiBaseUrl()}/admin/stats/executors?format=csv${
                  fromDateInput(applied.from)
                    ? `&from=${encodeURIComponent(fromDateInput(applied.from)!)}`
                    : ''
                }${
                  fromDateInput(applied.to, true)
                    ? `&to=${encodeURIComponent(fromDateInput(applied.to, true)!)}`
                    : ''
                }`}
                className="inline-flex items-center rounded-btn border-1 border-line-300 bg-surface-0 px-4 py-2 text-sm font-semibold text-brand-800 hover:bg-surface-100"
              >
                {t('admin.reports.csv')}
              </a>
            ) : null}
            <Button tone="secondary" onClick={() => setApplied(EMPTY_BOUNDS)}>
              {t('admin.inbox.filterReset')}
            </Button>
            <Button onClick={() => setApplied(bounds)}>{t('admin.common.apply')}</Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label={t('admin.inbox.filterFrom')}>
            <TextInput
              type="date"
              value={bounds.from}
              onChange={(event) => setBounds({ ...bounds, from: event.target.value })}
            />
          </Field>
          <Field label={t('admin.inbox.filterTo')}>
            <TextInput
              type="date"
              value={bounds.to}
              onChange={(event) => setBounds({ ...bounds, to: event.target.value })}
            />
          </Field>
          {/*
            The unassigned bucket, deliberately OUTSIDE the sortable table: a
            table sorted by any column would bury "nobody owns these" among the
            people who do own theirs.
          */}
          {stats ? (
            <div className="flex items-end pb-2">
              <p className="text-sm text-ink-700">
                <span className="font-semibold">{t('admin.executors.unassigned')}: </span>
                {t('admin.executors.unassignedCounts', {
                  open: stats.unassigned.open,
                  overdue: stats.unassigned.overdue_open,
                })}
              </p>
            </div>
          ) : null}
        </div>
      </Card>

      <ErrorBanner message={error} />

      <Card title={t('admin.executors.title')}>
        {loading && !stats ? <Spinner label={t('admin.common.loading')} /> : null}

        {stats && items.length === 0 ? (
          <EmptyState message={t('admin.executors.empty')} />
        ) : stats ? (
          <Table
            head={
              <tr>
                {COLUMNS.map((column) => (
                  <Th key={column.key}>
                    {/*
                      A real button, 100 % of the header cell: a sortable column
                      whose only affordance is a hover cursor is a hover-only
                      affordance, and the arrow states the CURRENT ordering
                      rather than a hypothetical one.
                    */}
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      aria-label={t(column.labelKey)}
                      className={`flex w-full items-center gap-1 text-left uppercase tracking-label ${
                        sortKey === column.key ? 'text-brand-700' : 'text-ink-500'
                      }`}
                    >
                      {t(column.labelKey)}
                      {sortKey === column.key ? <span>{ascending ? '↑' : '↓'}</span> : null}
                    </button>
                  </Th>
                ))}
                <Th>{t('admin.users.isActive')}</Th>
              </tr>
            }
          >
            {items.map((person) => (
              <Tr key={person.user_id}>
                <Td className="font-semibold text-ink-900">
                  {person.full_name}
                  <span className="ml-2 text-xs font-normal text-ink-500">
                    {t(`admin.role.${person.role}`)}
                  </span>
                </Td>
                <Td className="tabular-nums">{person.assigned_total}</Td>
                <Td className="tabular-nums">{person.open}</Td>
                <Td className="tabular-nums">
                  {person.overdue_open > 0 ? (
                    <span className="font-semibold text-danger-500">{person.overdue_open}</span>
                  ) : (
                    person.overdue_open
                  )}
                </Td>
                <Td className="tabular-nums">{person.answered}</Td>
                <Td className="tabular-nums">{person.closed}</Td>
                <Td className="tabular-nums">{person.reopened}</Td>
                <Td className="tabular-nums">{formatMetric(person.median_days_to_answer)}</Td>
                <Td className="tabular-nums">{formatPercent(person.on_time_pct)}</Td>
                <Td>
                  {person.is_active ? (
                    <Badge tone="success">{t('admin.common.yes')}</Badge>
                  ) : (
                    <Badge tone="neutral">{t('admin.devices.suspended')}</Badge>
                  )}
                </Td>
              </Tr>
            ))}
          </Table>
        ) : null}
      </Card>
    </div>
  );
}
